<template>
  <time
    :aria-label
    class="mood-inverse flex items-center gap-6 caption"
    :datetime="date.toISOString()"
    role="timer"
  >
    <span v-if="prefix" class="text-static-default-hi">{{ prefix }}</span>

    <span class="flex items-center gap-2">
      <span
        v-for="segment in segments"
        :key="segment.unit"
        class="caption-bold flex items-center justify-center rounded-xs p-4 tabular-nums text-static-default-hi"
        style="background-color: #191c20"
      >
        {{ segment.value }}{{ segment.unit }}
      </span>
    </span>
  </time>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, reactive } from 'vue'

const props = defineProps<{
  date: Date
  // Optional leading label, e.g. "ends in". Omitted for the on-image overlay.
  prefix?: string
  ariaLabel?: string
}>()

// Tick once a second so minutes stay current even though seconds aren't shown
// (days / hours / minutes only, per the Outlet design).
const remaining = reactive({ days: '00', hours: '00', minutes: '00' })

function pad(value: number): string {
  return Math.floor(Math.max(value, 0)).toString().padStart(2, '0')
}

function tick() {
  let diff = props.date.getTime() - Date.now()
  remaining.days = pad(diff / (1000 * 60 * 60 * 24))
  diff %= 1000 * 60 * 60 * 24
  remaining.hours = pad(diff / (1000 * 60 * 60))
  diff %= 1000 * 60 * 60
  remaining.minutes = pad(diff / (1000 * 60))
}

let interval: ReturnType<typeof setInterval> | undefined
onMounted(() => {
  tick()
  interval = setInterval(tick, 1000)
})
onUnmounted(() => {
  if (interval) clearInterval(interval)
})

const segments = computed(() => [
  { unit: 'd', value: remaining.days },
  { unit: 'h', value: remaining.hours },
  { unit: 'm', value: remaining.minutes },
])
</script>
